import { useState } from "react";
import api from "../lib/api";
import PodLayout from "../components/PodLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Brain, DownloadSimple, FileText, FilePdf, FileXls, FilePpt } from "@phosphor-icons/react";
import { toast } from "sonner";

const TEMPLATE_TYPES = [
  "Lesson Plan", "Termly Report", "Weekly Schedule", "Budget Sheet", "Staff Appraisal",
  "Parent Communication Letter", "Student Progress Report", "Curriculum Map",
  "Marketing Brochure", "Board Meeting Agenda", "Custom Template",
];

const FORMAT_ICONS = {
  pdf: FilePdf, docx: FileText, xlsx: FileXls, pptx: FilePpt,
};

export default function PodAiTemplatesPage() {
  const [type, setType] = useState("Lesson Plan");
  const [instructions, setInstructions] = useState("");
  const [format, setFormat] = useState("pdf");
  const [generating, setGenerating] = useState(false);

  const generate = async () => {
    if (!instructions.trim()) return toast.error("Enter instructions");
    setGenerating(true);
    try {
      // axios responseType blob
      const r = await api.post("/pod/ai-templates", { template_type: type, instructions, format }, { responseType: "blob" });
      const blob = new Blob([r.data]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${type.replace(/\s+/g, "_")}.${format}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Template generated — download started");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generation failed");
    }
    setGenerating(false);
  };

  return (
    <PodLayout>
      <div className="p-6 md:p-8 lg:p-12 max-w-4xl mx-auto" data-testid="pod-ai-templates-page">
        <div className="flex items-center gap-3 mb-3">
          <div className="bg-slate-900 text-amber-300 w-12 h-12 flex items-center justify-center"><Brain size={28} weight="fill" /></div>
          <div>
            <p className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-1">POD · AI</p>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-950" style={{ fontFamily: "'Outfit', sans-serif" }}>AI Template Generator</h1>
          </div>
        </div>
        <p className="text-sm text-slate-600 mb-8">Tell us what kind of document you need. AI will draft it for you — download as PDF, Word, Excel or PowerPoint.</p>

        <div className="space-y-4 bg-white border border-slate-200 p-6">
          <div>
            <Label>Template Type</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger className="rounded-none" data-testid="ai-template-type"><SelectValue /></SelectTrigger>
              <SelectContent>{TEMPLATE_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Instructions</Label>
            <Textarea value={instructions} onChange={e => setInstructions(e.target.value)} placeholder="Describe the document you need. Be specific — subject, grade level, sections, length, tone, etc." className="rounded-none min-h-[160px]" data-testid="ai-instructions" />
          </div>
          <div>
            <Label>Output Format</Label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-1">
              {["pdf", "docx", "xlsx", "pptx"].map(f => {
                const I = FORMAT_ICONS[f];
                return (
                  <button key={f} onClick={() => setFormat(f)} data-testid={`format-${f}`} className={`p-3 border flex flex-col items-center gap-1 ${format === f ? "border-blue-800 bg-blue-50 text-blue-900" : "border-slate-200 text-slate-600 hover:border-blue-300"}`}>
                    <I size={20} weight="fill" />
                    <span className="text-xs font-bold uppercase">{f}</span>
                  </button>
                );
              })}
            </div>
          </div>
          <Button onClick={generate} disabled={generating} className="bg-blue-800 text-white hover:bg-blue-900 rounded-none w-full py-6 text-base gap-2" data-testid="generate-template-btn">
            {generating ? "Generating…" : <><DownloadSimple size={16} weight="bold" /> Generate & Download</>}
          </Button>
        </div>
      </div>
    </PodLayout>
  );
}
